import { useFormContext } from 'react-hook-form';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Hooks
import useMarketingUrl from '@/utils/urls/useMarketingUrl';

import Button from '@/components/base/Button';
import Checkbox from '@/components/base/Checkbox';
// Components
import FormFieldMessage from '@/components/base/FormFieldMessage';
import Label from '@/components/base/Label';
import LinkBase from '@/components/base/LinkBase';
import ButtonOAuthSignIn from '@/components/composite/Button/ButtonOAuthSignIn';
// Type
import {
    SignUpFormPropsType,
    SignUpFormType
} from '@/components/pages/signup/createAccount/SignUpForm';
import { Trans } from '@/components/tools/translation';

type SignUpMethodChoiceFormType = {
    setIsWithEmail: SignUpFormPropsType['setIsWithEmail'];
};

const SignUpMethodChoiceForm = ({ setIsWithEmail }: SignUpMethodChoiceFormType) => {
    const { t } = useTranslation('signup_funnel');
    const { marketingUrl } = useMarketingUrl();
    const {
        register,
        setError,
        getValues,
        clearErrors,
        watch,
        formState: { errors }
    } = useFormContext<SignUpFormType>();

    return (
        <>
            <Button
                id="signup-with-email"
                type="button"
                className="mb-3"
                icon="email-clicks"
                onClick={() => {
                    if (getValues('terms')) {
                        setIsWithEmail(true);
                    } else {
                        setError('terms', {
                            type: 'manual',
                            message: t('create_account.error_terms')
                        });
                    }
                }}>
                {t('create_account.button_signup_with_email')}
            </Button>
            <ButtonOAuthSignIn
                id="signup-with-google"
                type="sign_up"
                channel="google"
                isAuthorize={() => {
                    if (!getValues('terms')) {
                        setError('terms', {
                            type: 'manual',
                            message: t('create_account.error_terms')
                        });
                        return false;
                    }
                    return true;
                }}
                queryParameters={watch('newsletter') ? { newsletter: 1 } : null}
            />
            <ButtonOAuthSignIn
                channel="apple"
                className="mt-3"
                id="signup-with-apple"
                type="sign_up"
                isAuthorize={() => {
                    if (!getValues('terms')) {
                        setError('terms', {
                            type: 'manual',
                            message: t('create_account.error_terms')
                        });
                        return false;
                    }
                    return true;
                }}
                queryParameters={watch('newsletter') ? { newsletter: 1 } : null}
            />
            <Label fontType="legalCheckbox" className="relative mt-5 pl-5">
                <Checkbox
                    id="newsletter"
                    boxSize="sm"
                    className="absolute left-0 top-0.5"
                    {...register('newsletter')}
                />
                {t('create_account.checkbox_newsletter')}
            </Label>
            <Label required fontType="legalCheckbox" className="relative mt-5 pl-5">
                <Checkbox
                    id="terms"
                    error={Boolean(errors?.terms)}
                    aria-describedby="terms_error"
                    boxSize="sm"
                    className="absolute left-0 top-0.5"
                    onClick={() => {
                        if (errors.terms) {
                            clearErrors('terms');
                        }
                    }}
                    {...register('terms', {
                        required: true
                    })}
                />
                <Trans
                    i18nKey="signup_funnel:create_account.checkbox_terms"
                    components={{
                        tag0: (
                            <LinkBase
                                key="terms"
                                href={marketingUrl('terms_of_use')}
                                target="_blank"
                                className="underline"
                                rel="noopener noreferrer"
                            />
                        ),
                        tag1: (
                            <LinkBase
                                key="subscription"
                                href={marketingUrl('terms_of_subscription')}
                                target="_blank"
                                className="underline"
                                rel="noopener noreferrer"
                            />
                        )
                    }}
                />
            </Label>
            {errors.terms ? (
                <div className="mt-1">
                    <FormFieldMessage id="terms_error" className="inline-block">
                        {t('create_account.error_terms')}
                    </FormFieldMessage>
                </div>
            ) : null}
        </>
    );
};

export default SignUpMethodChoiceForm;
